import { useEffect, useState } from 'react';
import { EditUserContextType } from '@/operator/components/users/context/EditUserContext';
import {
    FirestoreUser,
    FirestoreUserRole,
    UpdateFirestoreUserInput,
    useUpdateUserMutation,
    useUpdateUserRolesMutation,
} from 'generated/graphql';
import { useNotificationContext } from 'app/contexts/NotificationContext/index';
import { UserProps } from '@/operator/utils/forms/BasicForm';
import { isEqual } from 'lodash';
import useGetUser from '@/operator/graphql/hooks/users/useGetUser';
import { useAppSelector } from 'hooks/index';
import { selectPartnerData } from 'store/partner/partnerSelector';

const useEditUser = (userId: string): EditUserContextType => {
    const [loading, setLoading] = useState(true);
    const [selectedRole, setSelectedRole] = useState<FirestoreUserRole>();
    const [enableButtons, setEnableButtons] = useState(true);
    const [fetchedQualityUser, setFetchedQualityUser] =
        useState<Partial<FirestoreUser>>();

    const [updateUser] = useUpdateUserMutation();
    const [updateUserRoles] = useUpdateUserRolesMutation();
    const { showNotification } = useNotificationContext();

    const [localUpdatedUser, setLocalUpdatedUser] = useState<UserProps>();

    const { instanceId: organizationId } = useAppSelector(selectPartnerData);

    const { qualityUser } = useGetUser(userId, organizationId);

    const getUser = async () => {
        setFetchedQualityUser(qualityUser);
        const savedUserRole = qualityUser.roles.includes(
            FirestoreUserRole.OrganizationAdmin,
        )
            ? FirestoreUserRole.OrganizationAdmin
            : FirestoreUserRole.User;
        setSelectedRole(savedUserRole);
        setLocalUpdatedUser({
            firstName: qualityUser.firstName,
            lastName: qualityUser.lastName,
            email: qualityUser.email,
            displayName: qualityUser.displayName,
            pronouns: qualityUser.pronouns,
            avatarUrl: qualityUser.photoURL,
        });
        setLoading(false);
    };

    useEffect(() => {
        if (qualityUser) {
            getUser();
        }
    }, [qualityUser]);

    const updateUserRole = async () => {
        try {
            setEnableButtons(false);
            await updateUserRoles({
                variables: {
                    where: {
                        id: `${qualityUser.uid}_${qualityUser.instanceId}`,
                    },
                    input: {
                        roles: [selectedRole],
                    },
                },
            });
            setFetchedQualityUser({
                ...fetchedQualityUser,
                roles: [selectedRole],
            });
            showNotification({
                message: "User's role has been updated",
                severity: 'success',
            });
            setEnableButtons(true);
        } catch (error) {
            console.log('Error:', error);
            showNotification({
                message: `Unable to update role for user ${userId}`,
                severity: 'error',
            });
        }
    };

    const getNewUserUpdates = (): UserProps => {
        const originalUserValues: UserProps = {
            firstName: qualityUser.firstName,
            lastName: qualityUser.lastName,
            email: qualityUser.email,
            avatarUrl: qualityUser.photoURL,
            displayName: qualityUser.displayName,
            pronouns: qualityUser.pronouns,
        };

        const currentUserValues: UserProps = {
            firstName: localUpdatedUser.firstName,
            lastName: localUpdatedUser.lastName,
            email: localUpdatedUser.email,
            avatarUrl: localUpdatedUser.avatarUrl,
            displayName: localUpdatedUser.displayName,
            pronouns: localUpdatedUser.pronouns,
        };

        const userValuesFromDatabase = Object.values(originalUserValues);

        let userUpdates: UserProps = {};

        Object.entries(currentUserValues).forEach(
            ([userPropertyName, userValue], index) => {
                if (!isEqual(userValue, userValuesFromDatabase[index])) {
                    const newUserUpdates = {
                        [userPropertyName]: userValue,
                    };
                    userUpdates = Object.assign(userUpdates, newUserUpdates);
                }
            },
        );

        return userUpdates;
    };

    const updateUserBasicInformation = async () => {
        try {
            setEnableButtons(false);
            const newUserUpdates = getNewUserUpdates();

            if (Object.keys(newUserUpdates).length === 0) {
                setEnableButtons(true);
                return;
            }

            await updateUser({
                variables: {
                    uid: qualityUser.uid,
                    instanceId: qualityUser.organizationId,
                    input: {
                        firstName: newUserUpdates.firstName,
                        lastName: newUserUpdates.lastName,
                        displayName: newUserUpdates.displayName,
                        pronouns: newUserUpdates.pronouns,
                        photoURL: newUserUpdates.avatarUrl,
                    },
                },
            });
            setEnableButtons(true);
            setFetchedQualityUser({
                ...qualityUser,
                ...newUserUpdates,
            });

            showNotification({
                message: "User's basic information has been updated",
                severity: 'success',
            });
        } catch (error) {
            console.log('Error:', error);
            showNotification({
                message: `Unable to update basic information for user ${userId}`,
                severity: 'error',
            });
        }
    };

    return {
        loading,
        selectedRole,
        fetchedQualityUser,
        setSelectedRole,
        updateUserRole,
        enableButtons,
        localUpdatedUser,
        setLocalUpdatedUser,
        updateUserBasicInformation,
    };
};

export default useEditUser;
